import React, { useEffect, useState } from "react";
import { api, money, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/CrudModule";
import { GasPump as GasPumpIcon, Plus as PlusIcon, Trash as TrashIcon, FloppyDisk as FloppyDiskIcon, Camera as CameraIcon, UploadSimple as UploadSimpleIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

export function computeBalance(f) {
  const gallons = +(Number(f.meter_end || 0) - Number(f.meter_start || 0)).toFixed(2);
  const tiers = f.price_tiers || [];
  const expected = +tiers.reduce((s, t) => s + Number(t.gallons || 0) * Number(t.price || 0), 0).toFixed(2);
  const allocated = +tiers.reduce((s, t) => s + Number(t.gallons || 0), 0).toFixed(2);
  const totalExp = +(f.expenses || []).reduce((s, e) => s + Number(e.amount || 0), 0).toFixed(2);
  const collected = +(Number(f.cash_collected || 0) + Number(f.online_collected || 0)).toFixed(2);
  const cashToSubmit = +(collected - totalExp).toFixed(2);
  const shortOver = +(collected - totalExp - expected).toFixed(2);
  return { gallons, expected, allocated, totalExp, collected, cashToSubmit, shortOver };
}

export function ShortOver({ value }) {
  const v = Number(value);
  const cls = v < 0 ? "text-destructive" : v > 0 ? "text-amber-600" : "text-emerald-600";
  const label = v < 0 ? "Short" : v > 0 ? "Over" : "Balanced";
  return <span className={`font-semibold ${cls}`}>{money(value)} {v !== 0 && `(${label})`}</span>;
}

const emptyTiers = () => [
  { label: "Retail", gallons: "", price: "6.50" },
  { label: "Bulk / Cars", gallons: "", price: "5.85" },
];
const empty = () => ({ date: today(), meter_start: "", meter_end: "", cash_collected: "", online_collected: "", price_tiers: emptyTiers(), expenses: [], notes: "", meter_photos: [] });

export default function DriverPortal() {
  const [form, setForm] = useState(empty());
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => { api.get("/daily-balance").then((r) => setHistory(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const ch = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addExp = () => setForm((f) => ({ ...f, expenses: [...f.expenses, { description: "", amount: "" }] }));
  const chExp = (i, k, v) => setForm((f) => ({ ...f, expenses: f.expenses.map((e, idx) => idx === i ? { ...e, [k]: v } : e) }));
  const rmExp = (i) => setForm((f) => ({ ...f, expenses: f.expenses.filter((_, idx) => idx !== i) }));
  const addTier = () => setForm((f) => ({ ...f, price_tiers: [...f.price_tiers, { label: "", gallons: "", price: "" }] }));
  const chTier = (i, k, v) => setForm((f) => ({ ...f, price_tiers: f.price_tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
  const rmTier = (i) => setForm((f) => ({ ...f, price_tiers: f.price_tiers.filter((_, idx) => idx !== i) }));

  const c = computeBalance(form);
  const mismatch = form.meter_end && Math.abs(c.allocated - c.gallons) > 0.01;
  const remaining = +(c.gallons - c.allocated).toFixed(2);

  const weekly = history.reduce((a, h) => {
    const diff = (Date.now() - new Date(h.date).getTime()) / 86400000;
    if (diff <= 7) { a.gallons += Number(h.gallons_sold || 0); a.cash += Number(h.cash_collected || 0) + Number(h.online_collected || 0); }
    return a;
  }, { gallons: 0, cash: 0 });

  const processFile = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const max = 1280;
      let w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
      if (!w || !h) { URL.revokeObjectURL(url); resolve(null); return; }
      if (w > max || h > max) { if (w >= h) { h = Math.round((h * max) / w); w = max; } else { w = Math.round((w * max) / h); h = max; } }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      try { resolve(canvas.toDataURL("image/jpeg", 0.7)); }
      catch { resolve(null); }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(file);
    };
    img.src = url;
  });

  const onPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const slots = 6 - form.meter_photos.length;
    for (const file of files.slice(0, slots)) {
      const dataUrl = await processFile(file);
      if (dataUrl) setForm((f) => ({ ...f, meter_photos: [...f.meter_photos, dataUrl].slice(0, 6) }));
      else toast.error("Couldn't read that photo — try again");
    }
  };
  const rmPhoto = (i) => setForm((f) => ({ ...f, meter_photos: f.meter_photos.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    const hasTier = form.price_tiers.some((t) => Number(t.gallons) > 0 && Number(t.price) > 0);
    if (!form.meter_end || !hasTier) return toast.error("Enter meter readings and at least one price line with gallons");
    setSaving(true);
    try {
      await api.post("/daily-balance", {
        ...form,
        meter_start: Number(form.meter_start || 0), meter_end: Number(form.meter_end || 0),
        cash_collected: Number(form.cash_collected || 0),
        online_collected: Number(form.online_collected || 0),
        price_tiers: form.price_tiers
          .filter((t) => Number(t.gallons) > 0 || Number(t.price) > 0)
          .map((t) => ({ label: t.label || "Sale", gallons: Number(t.gallons || 0), price: Number(t.price || 0) })),
        expenses: form.expenses.map((e) => ({ description: e.description, amount: Number(e.amount || 0) })),
      });
      toast.success("Daily balance submitted for approval");
      setForm(empty()); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const Row = ({ label, hint, children }) => (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <div><div className="text-sm font-medium">{label}</div>{hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}</div>
      <div className="font-head font-semibold text-right">{children}</div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center gap-3">
        <span className="h-11 w-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center"><GasPumpIcon size={24} weight="duotone" /></span>
        <div><h1 className="font-head text-xl font-bold">Daily Butane Balance</h1><p className="text-muted-foreground text-sm">Enter meter readings, your price breakdown, cash and expenses, then submit.</p></div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="border rounded-lg bg-card p-4"><div className="text-xs text-muted-foreground">This Week — Gallons Sold</div><div className="font-head text-2xl font-bold mt-1">{weekly.gallons.toFixed(1)}</div></div>
        <div className="border rounded-lg bg-card p-4"><div className="text-xs text-muted-foreground">This Week — Cash Submitted</div><div className="font-head text-2xl font-bold mt-1">{money(weekly.cash)}</div></div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* inputs */}
        <div className="border rounded-lg bg-card p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Date</Label><Input data-testid="db-date" type="date" value={form.date} onChange={(e) => ch("date", e.target.value)} /></div>
            <div className="flex flex-col justify-end"><div className="text-xs text-muted-foreground">Total Gallons (meter)</div><div className="font-head text-lg font-bold">{c.gallons}</div></div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Meter Started</Label><Input data-testid="db-meter-start" type="number" value={form.meter_start} onChange={(e) => ch("meter_start", e.target.value)} /></div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Meter Ended</Label><Input data-testid="db-meter-end" type="number" value={form.meter_end} onChange={(e) => ch("meter_end", e.target.value)} /></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-muted-foreground">Price Breakdown (Retail / Bulk / Cars)</Label>
              <Button size="sm" variant="outline" onClick={addTier} data-testid="db-add-tier"><PlusIcon size={14} className="mr-1" />Add Price</Button>
            </div>
            <div className="space-y-2">
              {form.price_tiers.map((t, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input data-testid="tier-label" placeholder="Type" className="flex-1" value={t.label} onChange={(e) => chTier(i, "label", e.target.value)} />
                  <Input data-testid="tier-gallons" placeholder="Gallons" type="number" className="w-24" value={t.gallons} onChange={(e) => chTier(i, "gallons", e.target.value)} />
                  <Input data-testid="tier-price" placeholder="Price" type="number" className="w-24" value={t.price} onChange={(e) => chTier(i, "price", e.target.value)} />
                  <button onClick={() => rmTier(i)} className="p-2 rounded hover:bg-destructive/10 text-destructive"><TrashIcon size={16} /></button>
                </div>
              ))}
            </div>
            {mismatch && (
              <div className={`mt-2 text-xs rounded-md px-3 py-2 ${remaining > 0 ? "bg-amber-500/10 text-amber-600" : "bg-destructive/10 text-destructive"}`}>
                {remaining > 0 ? `${remaining} gal from the meter not yet allocated to a price.` : `${Math.abs(remaining)} gal over the meter total — check your gallons.`}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Cash Collected (BZ$)</Label><Input data-testid="db-cash" type="number" value={form.cash_collected} onChange={(e) => ch("cash_collected", e.target.value)} /></div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Online Collected (BZ$)</Label><Input data-testid="db-online" type="number" value={form.online_collected} onChange={(e) => ch("online_collected", e.target.value)} /></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-muted-foreground">Expenses</Label>
              <Button size="sm" variant="outline" onClick={addExp} data-testid="db-add-expense"><PlusIcon size={14} className="mr-1" />Add</Button>
            </div>
            <div className="space-y-2">
              {form.expenses.length === 0 && <div className="text-xs text-muted-foreground">No expenses added.</div>}
              {form.expenses.map((e, i) => (
                <div key={i} className="flex gap-2">
                  <Input placeholder="Description" value={e.description} onChange={(ev) => chExp(i, "description", ev.target.value)} />
                  <Input placeholder="Amount" type="number" className="w-28" value={e.amount} onChange={(ev) => chExp(i, "amount", ev.target.value)} />
                  <button onClick={() => rmExp(i)} className="p-2 rounded hover:bg-destructive/10 text-destructive"><TrashIcon size={16} /></button>
                </div>
              ))}
            </div>
          </div>
          <div><Label className="text-xs text-muted-foreground mb-1.5 block">Notes</Label><Input value={form.notes} onChange={(e) => ch("notes", e.target.value)} /></div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Meter Photos (proof — up to 6)</Label>
            <div className="flex gap-2 flex-wrap">
              <label className={`cursor-pointer text-xs inline-flex items-center gap-1.5 rounded-md bg-primary text-white px-3 py-2 hover:bg-primary/90 ${form.meter_photos.length >= 6 ? "opacity-50 pointer-events-none" : ""}`}>
                <CameraIcon size={15} weight="bold" /> Take Photo
                <input data-testid="db-photo-camera" type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={onPhoto} />
              </label>
              <label className={`cursor-pointer text-xs inline-flex items-center gap-1.5 rounded-md border px-3 py-2 hover:bg-secondary ${form.meter_photos.length >= 6 ? "opacity-50 pointer-events-none" : ""}`}>
                <UploadSimpleIcon size={15} weight="bold" /> Upload Photo
                <input data-testid="db-photo-upload" type="file" accept="image/*" multiple className="hidden" onChange={onPhoto} />
              </label>
            </div>
            {form.meter_photos.length > 0 && (
              <div className="mt-2 flex gap-2 flex-wrap">
                {form.meter_photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p} alt={`meter ${i + 1}`} className="h-20 w-20 rounded-md border object-cover" />
                    <button type="button" onClick={() => rmPhoto(i)} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-destructive text-white text-xs flex items-center justify-center">×</button>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[11px] text-muted-foreground mt-1">{form.meter_photos.length}/6 added · take a new photo or upload from your phone (Android & iPhone)</div>
          </div>
        </div>

        {/* live summary */}
        <div className="border rounded-lg bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/30 dark:to-slate-900 p-5 h-fit">
          <h3 className="font-head font-semibold mb-2">Balance Summary</h3>
          <Row label="Gallons Sold (meter)" hint="Meter Ended − Meter Started">{c.gallons}</Row>
          <Row label="Gallons Priced" hint="Allocated across price lines">{c.allocated}</Row>
          {form.price_tiers.filter((t) => Number(t.gallons) > 0).map((t, i) => (
            <Row key={i} label={t.label || "Sale"} hint={`${t.gallons} gal × ${money(t.price)}`}>{money(Number(t.gallons) * Number(t.price))}</Row>
          ))}
          <Row label="Expected Sales" hint="Sum of all price lines">{money(c.expected)}</Row>
          <Row label="Cash Collected">{money(form.cash_collected)}</Row>
          <Row label="Online Collected">{money(form.online_collected)}</Row>
          <Row label="Total Collected" hint="Cash + Online">{money(c.collected)}</Row>
          <Row label="Total Expenses">{money(c.totalExp)}</Row>
          <Row label="Cash to Submit" hint="Cash + Online − Expenses">{money(c.cashToSubmit)}</Row>
          <Row label="Short / Over" hint="Collected − Expenses − Expected"><ShortOver value={c.shortOver} /></Row>
          <Button className="w-full mt-4" onClick={submit} disabled={saving} data-testid="db-submit"><FloppyDiskIcon size={18} className="mr-1.5" />{saving ? "Submitting..." : "Submit for Approval"}</Button>
        </div>
      </div>

      {/* history */}
      <div className="border rounded-lg bg-card">
        <div className="p-4 border-b font-head font-semibold">My Submissions</div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-secondary/50">
              <TableHead>Date</TableHead><TableHead className="text-right">Gallons</TableHead>
              <TableHead className="text-right">Expected</TableHead><TableHead className="text-right">Cash</TableHead>
              <TableHead className="text-right">Online</TableHead>
              <TableHead className="text-right">Expenses</TableHead><TableHead className="text-right">Short/Over</TableHead>
              <TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {history.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No submissions yet</TableCell></TableRow>}
              {history.map((h) => (
                <TableRow key={h.id}>
                  <TableCell className="py-2">{fmtDate(h.date)}</TableCell>
                  <TableCell className="py-2 text-right">{h.gallons_sold}</TableCell>
                  <TableCell className="py-2 text-right">{money(h.expected_sales)}</TableCell>
                  <TableCell className="py-2 text-right">{money(h.cash_collected)}</TableCell>
                  <TableCell className="py-2 text-right">{money(h.online_collected || 0)}</TableCell>
                  <TableCell className="py-2 text-right">{money(h.total_expenses)}</TableCell>
                  <TableCell className="py-2 text-right"><ShortOver value={h.short_over} /></TableCell>
                  <TableCell className="py-2"><StatusBadge value={h.status === "approved" ? "ok" : "pending"} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
